import React, { useEffect, useState } from "react";
import Markdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { CalendarWeek as CalendarIcon, Clock, ArrowRight as IconRight } from "flowbite-react-icons/outline";
import { Check as IconCheck } from "flowbite-react-icons/outline";
import AiIcon from "../../components/Icons/AiIcon";
import CustomButton from "../../components/CustomButton";
import Loading from "../../components/Loading";
import Api from "../../services/Api";
import {getLanguage} from "../../lang/helpers";
import ProgressBar from "../../components/Progress/ProgressBar";

interface AssessmentInterface {
  name: string;
  status: string;
  completed?: string;
}


const ProfileMenu: React.FC = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const [assessments, setAssessments] = useState<AssessmentInterface[]>([]);
  const lang = getLanguage(i18n.language);
  const [profileSummary, setProfileSummary] = useState<string | null>(null);
  const [completeAssessments, setCompleteAssements] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    api.get("client/profile").then((response) => {
      if (response.json.assessments) {
        setAssessments(response.json.assessments);
        setCompleteAssements(response.json.completeAssessments);
      }
      setProfileSummary(response.json.profileSummary);
      setLoaded(true);
    });
  };

  const getName = (name: string)=> {
    if (name === "big5") {
      return lang === "sv" ? "Personlighetstest" : "Personality test";
    } else if (name === "stress") {
      return lang === "sv" ? "Stresstest" : "Stress test"
    } else {
      return "N/A: " + name
    }
  }

  const getDescription = (name: string)=> {
    if (name === "big5") {
      return  lang === "sv" ? "Upptäck din unika personlighetstyp" : "Discover your unique personality type";
    } else if (name === "stress") {
      return lang === "sv" ? "Få insikter om din stressnivå" : "Understand your current stress level";
    } else {
      return "N/A: " + name
    }
  }

  const goTo = (name: string) => {
    if (name === "big5") {
      navigate("/profile/big5");
    } else if (name === "stress") {
      navigate("/profile/stress");
    } else {
      alert("Can't navigate to " + name);
    }
  }

  const testTime = (name: string) => {
    if (name === "big5") {
      return 20;
    } else if (name === "stress") {
      return 5;
    } else {
      return 10;
    }
  }

  const buttonText = (assessment: AssessmentInterface) => {
    if (assessment.completed) {
      return lang === "sv" ? "Se resultat" : "See result";
    } else if (assessment.status === "pending") {
      return lang === "sv" ? "Fortsätt testet" : "Continue assessment";
    } else {
      return lang === "sv" ? "Starta test" : "Start assessment";
    }
  }

  const renderProfileSummary = () => {
    if (profileSummary) {
      return (
        <>
          <div className="mt-6 z-heading2">{lang === "sv" ? "Profilanalys" : "Profile analysis"}</div>
          <div className="mt-2 text-gray-400">
            {
              lang === "sv" ? "Baserat på dina färdiga tester." :
                "Based on your completed assessments."
            }
          </div>
          <div className="mt-4 bg-indigo-900 px-4 rounded-md overflow-hidden markdown-content no-headers text-gray-300 border-indigo-800 border-1">
            <Markdown>{profileSummary}</Markdown>
          </div>
        </>
      )
    }

    return (
      <>
        <div className="mt-6 z-heading2">{t("overview")}</div>
        <div className="mt-2 text-gray-400">
          {
            lang === "sv" ? "Gör testerna för att skapa din unika profil" :
              "Complete the assessments to unlock your personal analysis"
          }
        </div>
        <div className="mt-4 bg-indigo-900 p-4 rounded-md border-indigo-800 border-1">
            <AiIcon alt="ai" className="h-8 w-8 mx-auto text-center"/>
            <div className="mt-2 text-gray-400 text-center">
              {completeAssessments} {lang === "sv" ? "av" : "of"} 2&nbsp;
              {
                lang === "sv" ? "tester färdiga" :
                  "assessments completed"
              }
            </div>
          {
            completeAssessments > 0 &&
              <div className="mt-4">
                <ProgressBar step={completeAssessments} steps={2} hideSteps={true}/>
              </div>
          }
        </div>
      </>
    )
  }

  const renderAssessments = () => {
    return (
      <>
        {
          assessments.map(assessment => {
            const buttonType = assessment.completed ? "outlineWhiteBase" : "blueBase"
            const iconRightColor = assessment.completed ? "text-white" : "text-neutral-900!"
            return (
              <div className="mt-4 bg-indigo-900 p-4 rounded-md border-indigo-800 border-1" key={"assessment_" + assessment.name}>
              <div className="flex justify-between">
                  <p className="font-medium">{getName(assessment.name)}</p>
                  {
                    assessment.completed &&
                    <IconCheck className="h-5 w-5 text-success-500!" aria-hidden="true" />
                  }
                </div>

                <div className="text-gray-400">{getDescription(assessment.name)}</div>
                {
                  assessment.completed ? (
                    <div className="mt-4 flex items-center">
                      <CalendarIcon className="h-4 w-4" aria-hidden="true"/>
                      <div className="z-title3 ml-2">{assessment.completed.substring(0, 10)}</div>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-center">
                      <Clock className="h-4 w-4"/>
                      <div className="z-title3 ml-2">{testTime(assessment.name)} Min</div>
                    </div>
                  )
                }
                <CustomButton buttonType={buttonType} className="mt-4 flex items-center justify-center" onClick={() => goTo(assessment.name)}
                              rightIcon={<IconRight className={`h-4 w-4 ${iconRightColor}`} aria-hidden="true" />}>
                  {buttonText(assessment)}
                </CustomButton>
              </div>
            )
          })
        }
      </>
    )
  }

  if (!loaded) {
    return <Loading txt={lang === "sv" ? "Laddar tester..." : "Loading assessments..."}/>;
  }

  return (
    <div className="main-one-col small">
      <div className="z-heading1 mt-6">{lang === "sv" ? "Profil" : "Profile"}</div>
      <div className="mt-2 text-gray-400">
        {
          lang === "sv" ? "Få värdefulla insikter om vem du är och utveckla en djupare förståelse för dig själv." :
            "Discover key insights about who you are and build a deeper understanding of yourself."
        }
      </div>


      {renderProfileSummary()}

      <div className="mt-6 z-heading2">{lang === "sv" ? "Tester" : "Assessments"}</div>

      {renderAssessments()}

    </div>
  );
};

export default ProfileMenu;
